import React, { useState } from "react";
import { X, Video, Link2, Copy, Check, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import conferenceService from "../../services/conferenceService";

const conferencingOptions = [
	{
		id: "gmeet",
		name: "Google Meet",
		description: "Create a new Google Meet video meeting",
		icon: Video,
	},
	{
		id: "custom",
		name: "Custom link",
		description: "Add a custom video conferencing link",
		icon: Link2,
	},
];

const ConferencingModal = ({ conferencing, onChange, onClose, eventTitle }) => {
	const { tokens } = useAuth();
	const [selectedOption, setSelectedOption] = useState(
		conferencing?.type || ""
	);
	const [customLink, setCustomLink] = useState(conferencing?.url || "");
	const [error, setError] = useState("");
	const [copied, setCopied] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!selectedOption) return;

		if (selectedOption === "gmeet" && (!tokens || !tokens.access_token)) {
			setError("Authentication required. Please sign in again.");
			return;
		}

		setIsCreating(true);
		setError("");

		try {
			let conferenceData;

			if (selectedOption === "custom") {
				try {
					new URL(customLink);
					conferenceData = {
						type: "custom",
						url: customLink,
					};
				} catch {
					setError("Please enter a valid URL");
					setIsCreating(false);
					return;
				}
			} else if (selectedOption === "gmeet") {
				const meetData = await conferenceService.createGoogleMeet(
					tokens,
					eventTitle
				);
				// Let's log what we're getting from the API
				console.log("API Response:", meetData);

				conferenceData = {
					type: "gmeet",
					url: meetData.url,
					id: meetData.id,
				};
				// Log what we're passing back
				console.log("Conference Data being passed:", conferenceData);
			}

			if (conferenceData) {
				// Make sure we're calling onChange with the right data
				onChange(conferenceData);
				onClose();
			}
		} catch (error) {
			console.error("Failed to create conference:", error);
			setError(
				error.message || "Failed to create conference. Please try again."
			);
		} finally {
			setIsCreating(false);
		}
	};

	const handleCopyLink = async () => {
		if (conferencing?.url) {
			try {
				await navigator.clipboard.writeText(conferencing.url);
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			} catch (err) {
				console.error("Failed to copy:", err);
				setError("Failed to copy link to clipboard");
			}
		}
	};

	const handleClear = () => {
		onChange(null);
		onClose();
	};

	return (
		<>
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={onClose}
			/>

			<div className="fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl">
				<form onSubmit={handleSubmit}>
					{/* Header */}
					<div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							{conferencing ? "Video conferencing" : "Add video conferencing"}
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
						>
							<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
						</button>
					</div>

					<div className="p-4 space-y-4">
						{/* Current conference display */}
						{conferencing && (
							<div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
								<div className="text-sm font-medium text-gray-700 dark:text-gray-300">
									Current meeting
								</div>
								<div className="flex flex-col gap-2 text-gray-600 dark:text-gray-300 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg px-3 py-2">
									<div className="flex items-center gap-2 min-w-0">
										<Video className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
										<span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
											{eventTitle || "Untitled Meeting"}
										</span>
									</div>
									<div className="flex items-center justify-between gap-2">
										<span className="text-sm truncate">{conferencing.url}</span>
										<button
											type="button"
											onClick={handleCopyLink}
											className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors shrink-0"
										>
											{copied ? (
												<Check className="w-4 h-4 text-green-500" />
											) : (
												<Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
											)}
										</button>
									</div>
								</div>
							</div>
						)}

						{!isCreating && !conferencing && (
							<>
								{/* Options Grid */}
								<div className="grid grid-cols-1 gap-2">
									{conferencingOptions.map(
										({ id, name, description, icon: Icon }) => (
											<button
												key={id}
												type="button"
												onClick={() => {
													setSelectedOption(id);
													setError("");
												}}
												className={`p-3 rounded-lg flex items-start gap-3 text-left transition-colors
													${
														selectedOption === id
															? "bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
															: "hover:bg-gray-100/50 dark:hover:bg-gray-800/50 text-gray-900 dark:text-gray-100"
													}`}
											>
												<Icon
													className={`w-5 h-5 mt-0.5 shrink-0 ${
														selectedOption === id
															? "text-blue-500 dark:text-blue-400"
															: "text-gray-500 dark:text-gray-400"
													}`}
												/>
												<div>
													<div className="font-medium">{name}</div>
													<div
														className={`text-sm ${
															selectedOption === id
																? "text-blue-500 dark:text-blue-400"
																: "text-gray-500 dark:text-gray-400"
														}`}
													>
														{description}
													</div>
												</div>
											</button>
										)
									)}
								</div>

								{/* Custom Link Input */}
								{selectedOption === "custom" && (
									<div className="space-y-2">
										<input
											type="url"
											value={customLink}
											onChange={(e) => {
												setCustomLink(e.target.value);
												setError("");
											}}
											placeholder="Enter meeting link"
											className={`w-full px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg 
												text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400
												focus:outline-none focus:ring-2 
												${error ? "ring-2 ring-red-500" : "focus:ring-blue-500"}`}
										/>
									</div>
								)}
							</>
						)}

						{isCreating && (
							<div className="py-8 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 space-y-3">
								<Loader2 className="w-8 h-8 animate-spin" />
								<div className="text-sm">Creating meeting...</div>
							</div>
						)}

						{/* Error Message */}
						{error && (
							<p className="text-sm text-red-500 dark:text-red-400">{error}</p>
						)}
					</div>

					{/* Footer */}
					<div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-800">
						{conferencing && (
							<button
								type="button"
								onClick={handleClear}
								className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
							>
								Remove
							</button>
						)}
						<button
							type="button"
							onClick={onClose}
							disabled={isCreating}
							className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
						>
							Cancel
						</button>
						{!conferencing && (
							<button
								type="button"
								onClick={handleSubmit}
								disabled={
									isCreating ||
									!selectedOption ||
									(selectedOption === "custom" && !customLink)
								}
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
							>
								{isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
								{isCreating ? "Creating..." : "Add"}
							</button>
						)}
					</div>
				</form>
			</div>
		</>
	);
};

export default ConferencingModal;
